import { useState, useEffect } from 'react'
import _ from 'lodash'
import fileDownload from 'js-file-download'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { RichTreeView } from '@mui/x-tree-view/RichTreeView'

import FileUploadPopup from '/utils/file-upload-popup'

const ManageInstanceData = ({ open, close, instanceData, setInstanceData }) => {
  try {
    const [newManagedInstanceData, setNewManagedInstanceData] = useState(instanceData)
    const [dataTree, setDataTree] = useState([])
    const [expandedItems, setExpandedItems] = useState([])

    // List of items selected in the tree (ids only)
    const [selectedItems, setSelectedItems] = useState([])
    // Actual selected item object
    const [selectedNode, setSelectedNode] = useState()

    // Manage open state of our file upload popup
    const [fileUploadPopupOpen, setFileUploadPopupOpen] = useState(false)

    useEffect(() => {

      try {
        if (open) {
          setNewManagedInstanceData(_.cloneDeep(instanceData))
          instanceDataToTree(instanceData)
        }
      }
      catch (err) {
        throw err
      }
    }, [open])


    const getkeyPaths = (obj, currentPath = '', result = []) => {
      if (typeof obj !== 'object' || obj === null) {
        return result
      }

      for (const property in obj) {
        if (obj.hasOwnProperty(property)) {
          const newPath = currentPath === '' ? property : `${currentPath}.${property}`

          if (Array.isArray(obj[property])) {
            obj[property].forEach((item, index) => {
              getkeyPaths(item, `${newPath}[${index}]`, result)
            })
          } else {
            getkeyPaths(obj[property], newPath, result)
          }

          result.push(newPath.replace("[", ".").replace("]", ""))
        }
      }
      return result
    }

    const instanceDataToTree = data => {
      try {
        let newDataTree = instanceDataToArray(data)

        setDataTree([{ id: "___root___", label: "Instance Data", children: newDataTree }])

        // Get an array of all leaf paths in data
        let allPaths = getkeyPaths(data)

        // This will be an array of all branches (not leaves)
        let newExpandedItems = ["___root___"]

        allPaths.forEach(leaf => {
          // Find branch above each leaf
          let leafArray = leaf.split(".")

          while (leafArray.length) {
            leafArray.pop()

            // Level 1 leaves will not have a parent branch...
            if (leafArray.length) {
              let branch = leafArray.join(".")
              newExpandedItems.push(branch)
            }
          }
        })

        setExpandedItems(_.uniq(newExpandedItems)) // remove duplicate branches
      }
      catch (err) {
        throw err
      }
    }



    const instanceDataToArray = (object, path = []) => {

      let children = []

      for (let i in object) {
        let itemPath = _.clone(path)
        itemPath.push(i)

        let item = { id: itemPath.join("."), label: i }
        if (typeof object[i] === "object") {
          item.children = instanceDataToArray(object[i], itemPath)
        } else {
          item.value = object[i]
        }
        children.push(item)
      }

      return _.sortBy(children, ["id"])
    }

    const handleClose = (e, reason) => {
      if (reason && reason === "backdropClick")
        return
      close()
    }

    const save = () => {
      // Save newManagedInstanxceData back to instanceData
      setInstanceData(newManagedInstanceData)
      close()
    }

    const handleSelectedItemChange = (e, itemId) => {
      // Set the selected node ID
      setSelectedItems([itemId])

      // Find the node object
      let path = itemId
      let id = itemId.split(".").pop()
      let value = (id !== "___root___") ? _.get(newManagedInstanceData, itemId) : {}
      setSelectedNode({ id, path, value })
    }

    const updateSelectedItemType = (newType) => {

      try {
        // Change the type of the selected node
        let newSelectedNode = _.cloneDeep(selectedNode)

        if (newType === "leaf") newSelectedNode.value = ""
        if (newType === "branch") newSelectedNode.value = { child1: "" }

        // Set our new data as the "selectedItem"
        setSelectedNode(newSelectedNode)
        setSelectedItems([newSelectedNode.path])

        // Update our instance data
        let ndd = _.cloneDeep(newManagedInstanceData)
        _.set(ndd, newSelectedNode.path, newSelectedNode.value)
        setNewManagedInstanceData(ndd)

        // Refresh our tree content
        instanceDataToTree(ndd)
      }
      catch (err) {
        throw err
      }
    }

    const updateNodeId = (e) => {

      let newNodeId = e.target.value

      // Should check that there isn't another node with the same name
      // Otherwisde we will overwrite the other node...

      let si = _.cloneDeep(selectedNode)
      si.id = newNodeId
      si.label = newNodeId
      setSelectedNode(si)
      sendNodeToTree(si)
    }

    const updateNodeValue = (e) => {
      let si = _.cloneDeep(selectedNode)
      si.value = e.target.value
      setSelectedNode(si)
      sendNodeToTree(si)
    }

    // Save our new selected Node data back to the newManagedInstanceData / Tree
    const sendNodeToTree = (si) => {
      // Store old path so we can remove the old node 
      let oldPath = si.path

      // Update path with our new id
      let p = si.path.split(".")
      p.pop()
      p.push(si.id)
      si.path = p.join(".")

      // Update our instance data
      let ndd = _.cloneDeep(newManagedInstanceData)
      ndd = _.omit(ndd, oldPath)
      _.set(ndd, si.path, si.value)
      setNewManagedInstanceData(ndd)

      // Refresh our tree content
      instanceDataToTree(ndd)

      // Set our updated selectedItems / selectedNode
      setSelectedItems([si.path])
      setSelectedNode(si)
    }

    const addNode = () => {
      let si = _.cloneDeep(selectedNode)

      let id = "newItem"
      let path = (selectedNode.id === "___root___") ? id : si.path + "." + id
      let value = ""

      let newNode = { id, path, value }
      setSelectedNode(newNode)
      setSelectedItems([path])

      // Update our instance data
      let ndd = _.cloneDeep(newManagedInstanceData)
      _.set(ndd, path, value)
      setNewManagedInstanceData(ndd)

      // Refresh our tree content
      instanceDataToTree(ndd)
    }

    const deleteNode = () => {
      // Update our instance data
      let ndd = _.cloneDeep(newManagedInstanceData)
      ndd = _.omit(ndd, selectedNode.path)
      setNewManagedInstanceData(ndd)

      // Refresh our tree content
      instanceDataToTree(ndd)

      setSelectedItems([])
      setSelectedNode(null)
    }


    const downloadInstanceData = () => fileDownload(JSON.stringify(newManagedInstanceData, null, 2), "data.json")
    const openFileUpload = () => setFileUploadPopupOpen(true)
    const closeFileUpload = () => setFileUploadPopupOpen(false)
    const saveUploadedFile = file => {
      try {
        let data = JSON.parse(file)
        setInstanceData(data)
        instanceDataToTree(data)
      }
      catch (err) {
        // Alert user that data is not valid
        console.log("uploaded data is not valid JSON")
      }
    }

    if (open && expandedItems.length) return (
      <Dialog onClose={handleClose} open={open} sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "1200px",
            maxWidth: "1200px",
            height: "800px"
          },
        },
      }}>

        <div className="popupFieldEditor" style={{ width: 1200, height: 800 }}>
          <DialogTitle>Manage Sample Data</DialogTitle>

          <div className="flex flex-row justify-between gap-5" style={{ maxHeight: 800 - 195 }}>
            <div className="scrollable_y" style={{ width: 400, minHeight: 600 }}>
              <RichTreeView
                items={dataTree}
                expandedItems={expandedItems}
                selectedItems={selectedItems}
                onSelectedItemsChange={handleSelectedItemChange}
              />
            </div>
            <div style={{ width: 600 }}>
              <Editor
                selectedNode={selectedNode}
                updateSelectedItemType={updateSelectedItemType}
                updateNodeId={updateNodeId}
                updateNodeValue={updateNodeValue}
                sendNodeToTree={sendNodeToTree}
                deleteNode={deleteNode}
                addNode={addNode}
              />
            </div>
          </div>

          <hr />

          <div className="flex flex-row w-full justify-between">
            <div className="flex flex-row justify-between gap-5">
              <Button variant="outlined" onClick={openFileUpload}>Upload</Button>
              <Button variant="outlined" onClick={downloadInstanceData}>Download</Button>
            </div>

            <div style={{ paddingLeft: 100, paddingRight: 100 }}><b>NOTE:</b> Save button saves data to edit window only.<br />
              You <b>must save</b> there as well to make changes permanent.</div>

            <div className="flex flex-row gap-5 justify-between">
              <Button variant="outlined" onClick={close}>Cancel</Button>
              <Button variant="contained" onClick={save}>Save</Button>
            </div>
          </div>
        </div>

        <FileUploadPopup open={fileUploadPopupOpen} close={closeFileUpload} accept=".json" save={saveUploadedFile} />
      </Dialog>
    )
  }
  catch (err) {
    throw err
  }
}

export default ManageInstanceData

const Editor = ({ selectedNode, updateSelectedItemType, updateNodeId, updateNodeValue, addNode, deleteNode }) => {

  if (selectedNode) return (
    <Box
      component="form"
      sx={{ '& .MuiTextField-root': { m: 1, width: '420px' } }}
      noValidate
      autoComplete="off"
    >
      <TextField
        slotProps={{ inputLabel: { shrink: true } }}
        label="path"
        value={selectedNode?.path}
        disabled={true}
      />

      <ValueEditor
        selectedNode={selectedNode}
        updateSelectedItemType={updateSelectedItemType}
        updateNodeId={updateNodeId}
        updateNodeValue={updateNodeValue}
        deleteNode={deleteNode}
        addNode={addNode}
      />
    </Box>
  )
}


const ValueEditor = ({ selectedNode, updateSelectedItemType, updateNodeId, updateNodeValue, addNode, deleteNode }) => {
  if (typeof selectedNode.value === "object" && selectedNode.value !== null)
    return (
      <BranchEditor
        selectedNode={selectedNode}
        updateSelectedItemType={updateSelectedItemType}
        updateNodeId={updateNodeId}
        deleteNode={deleteNode}
        addNode={addNode}
      />
    )
  else
    return (
      <LeafEditor
        selectedNode={selectedNode}
        updateSelectedItemType={updateSelectedItemType}
        updateNodeId={updateNodeId}
        updateNodeValue={updateNodeValue}
        deleteNode={deleteNode}
      />
    )
}


const BranchEditor = ({ selectedNode, updateSelectedItemType, updateNodeId, deleteNode, addNode }) => {

  const convertToLeaf = () => {
    updateSelectedItemType("leaf")
  }

  const checkForEnterkey = e => {
    if (e.code === "Enter")
      updateNodeId(e)
  }

  return (
    <div style={{ width: 500, maxWidth: 500, minWidth: 500 }}>
      <Typography variant="h3" sx={{ p: 2 }}>Branch</Typography>

      <TextField
        slotProps={{ inputLabel: { shrink: true } }}
        label="id"
        defaultValue={selectedNode.id}
        onKeyUp={checkForEnterkey}
        onBlur={updateNodeId}
        disabled={selectedNode.id.match(/__.*__/)}
      />

      <div className="flex flex-row gap-5 pt-5">
        <Button variant="contained" className="text-nowrap" style={{ minWidth: 180, maxHeight: 40 }} onClick={addNode}>Add child</Button>
        <Typography variant="caption">Add a new child node below this one.</Typography>
      </div>

      {selectedNode.id !== "___root___" &&
        <>
          <div className="flex flex-row gap-5 pt-5">
            <Button variant="outlined" color="warning" className="text-nowrap" style={{ minWidth: 180, maxHeight: 40 }} disabled={selectedNode?.id.match(/__.*__/)} onClick={deleteNode}>Delete node</Button>
            <Typography variant="caption">Any child nodes will also be deleted</Typography>
          </div>

          <div className="flex flex-row gap-5 pt-5">
            <Button variant="outlined" color="error" className="text-nowrap" style={{ minWidth: 180, maxHeight: 40 }} disabled={selectedNode?.id.match(/__.*__/)} onClick={convertToLeaf}>Convert to leaf</Button>
            <Typography variant="caption">Converting to a leaf will delete any child nodes. This may break your existing pass layout...</Typography>
          </div>
        </>
      }
    </div>
  )
}

const LeafEditor = ({ selectedNode, updateSelectedItemType, updateNodeId, updateNodeValue, deleteNode }) => {

  const [nodeId, setNodeId] = useState()

  useEffect(() => {
    setNodeId(selectedNode.id)
  },[selectedNode])
  
  if (selectedNode) {

    const convertToBranch = () => {
      updateSelectedItemType("branch")
    }

    return (
      <div style={{ width: 500, maxWidth: 500, minWidth: 500 }}>
        <Typography variant="h3" sx={{ p: 2 }}>Leaf</Typography>

        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label="id"
          value={nodeId}
          onChange={e => setNodeId(e.target.value)}
          onBlur={updateNodeId}
          disabled={selectedNode.id.match(/__.*__/)}
          helperText={
            <>
            <div className='mb-4'>ID is any unique name that you wish to use to identify this data value. eg. membership_number</div>
            <div className='font-semibold mb-4'>NOTE: All data is encrypted when stored. This means it is not searchable. To make a field searcheable, prefix the ID with *_. eg. *_membership_number"</div>
            <div>Be aware that searchable data is NOT encrypted, so avoid making sensitive data searcheable.</div>
            </>}
        />
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label="value"
          value={selectedNode.value}
          onChange={updateNodeValue}
          multiline
          minRows={1}
          maxRows={10}
          disabled={selectedNode.id.match(/__pass_id__/)}
        />

        <div className="flex flex-row gap-5 pt-5">
          <Button variant="outlined" color="warning" className="text-nowrap" disabled={selectedNode?.id.match(/__.*__/)} style={{ minWidth: 180, maxHeight: 40 }} onClick={deleteNode}>Delete node</Button>
          <Typography variant="caption">Any child nodes will also be deleted</Typography>
        </div>

        <div className="flex flex-row gap-5 pt-5">
          <Button variant="outlined" color="error" className="text-nowrap" disabled={selectedNode?.id.match(/__.*__/)} style={{ minWidth: 180, maxHeight: 40 }} onClick={convertToBranch}>Convert to branch</Button>
          <Typography variant="caption">Converting to a branch will delete this node's value and create a new child node. This may break your existing pass layout...</Typography>
        </div>
      </div>
    )
  }
} 